import { type ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import type { UserRole } from '../api/schema';

import { useSession } from './session';

/** Shown while the session token is being resolved against the backend. */
function SessionLoading(): React.JSX.Element {
  return (
    <div
      role="status"
      aria-busy="true"
      style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)' }}
    >
      <span className="ds-spinner" aria-hidden="true" />
      <span className="sr-only">Checking your session…</span>
    </div>
  );
}

export interface RequireAuthProps {
  children?: ReactNode;
}

/**
 * Gate for authenticated routes: while the session resolves, shows a busy
 * indicator; if unauthenticated, redirects to /login remembering where the user
 * was headed; otherwise renders the route.
 */
export function RequireAuth({ children }: RequireAuthProps): React.JSX.Element {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') return <SessionLoading />;
  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children ?? <Outlet />}</>;
}

export interface RequireRoleProps {
  /** Roles permitted through this gate. */
  allow: readonly UserRole[];
  children?: ReactNode;
  /** Where to send a permitted-but-wrong-role user. Defaults to /403. */
  redirectTo?: string;
}

/**
 * Gate for role-restricted routes (the ADMIN area). Mirrors the backend RBAC so
 * the UI never offers what the API refuses. Assumes it sits inside `RequireAuth`
 * (an unauthenticated user is redirected there first); a wrong role lands on /403.
 */
export function RequireRole({ allow, children, redirectTo = '/403' }: RequireRoleProps): React.JSX.Element {
  const { status, role } = useSession();

  if (status === 'loading') return <SessionLoading />;
  if (status === 'unauthenticated') return <Navigate to="/login" replace />;
  if (role === null || !allow.includes(role)) return <Navigate to={redirectTo} replace />;
  return <>{children ?? <Outlet />}</>;
}
